import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import Alerty from '../../common/Alerty';
import ReactSelect from 'react-select';
import ExpenseHeader from './elements/ExpenseHeader';
import ExpenseFilters from './elements/ExpenseFilters';
import ExpenseList from './elements/ExpenseList';
import Pagination from './elements/Pagination';
import usePermissions from '../../common/usePermissions';
import { rootUrl } from '../../../config/config';
import { buildExpenseListParams } from './expenseViewConfig';

const monthLabelFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });
const pad = (value) => String(value).padStart(2, '0');

const buildMonthOptions = (monthsBack = 24) => {
    const now = new Date();
    const options = [];

    for (let i = 0; i < monthsBack; i += 1) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        options.push({ value: `${year}-${month}`, label: monthLabelFormatter.format(date) });
    }

    return options;
};

const buildYearOptions = (yearsBack = 6) => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: yearsBack }, (_, idx) => currentYear - idx);
};

const selectStyles = {
    control: (provided, state) => ({
        ...provided,
        minHeight: '42px',
        borderRadius: '0.375rem',
        borderColor: state.isFocused ? '#FF5733' : '#d1d5db',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(255,87,51,0.12)' : 'none',
        '&:hover': {
            borderColor: '#FF5733'
        }
    }),
    menu: (provided) => ({ ...provided, zIndex: 30 }),
    menuPortal: (provided) => ({ ...provided, zIndex: 50 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#fff7ed' : state.isSelected ? '#ffedd5' : '#fff',
        color: '#1f2937'
    })
};

const formatDate = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatMoney = (value) => {
    const amount = Number(value || 0);
    return `KES ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ExpenseApprovals = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();

    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [, setCounts] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [alert, setAlert] = useState({ color: '', message: '' });
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [cars, setCars] = useState([]);
    const [referenceDataLoaded, setReferenceDataLoaded] = useState(false);
    const [referenceDataLoading, setReferenceDataLoading] = useState(false);
    const [filters, setFilters] = useState({
        expense_type: '',
        category: '',
        sub_category: '',
        plate_no: '',
        min_amount: '',
        max_amount: ''
    });
    const [periodView, setPeriodView] = useState('all');
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
    });
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedWeekKey, setSelectedWeekKey] = useState('');
    const [systemWeeks, setSystemWeeks] = useState([]);
    const [systemWeeksLoaded, setSystemWeeksLoaded] = useState(false);
    const [systemWeeksLoading, setSystemWeeksLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [pageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [, setCountsLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));

    const canEditExpense = hasPermission('expenses.edit');
    const canApproveExpense = hasPermission('expenses.approve');

    const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

    const normalizeExpenseRow = useCallback((expense) => {
        if (!expense) return expense;
        return { ...expense, expense_id: expense.expense_id ?? expense.id };
    }, []);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const showAlert = useCallback((color, message) => {
        setAlert({ color, message });
        setOpen(true);
        setTimeout(() => setOpen(false), 5000);
    }, []);

    const fetchReferenceData = useCallback(async () => {
        if (referenceDataLoaded || referenceDataLoading) return;
        setReferenceDataLoading(true);
        try {
            const [categoriesRes, carsRes] = await Promise.all([
                axios.get(`${rootUrl}/api/expenses/categories`, { headers: authHeaders }),
                axios.get(`${rootUrl}/cars`, { headers: authHeaders })
            ]);

            if (categoriesRes.data?.success) {
                const cats = (categoriesRes.data.data || []).map((c) => ({
                    category_id: c.id,
                    category_name: c.name,
                    code: c.code,
                    description: c.description,
                    is_vehicle_related: c.is_vehicle_related,
                    is_office_related: c.is_office_related,
                    is_active: c.is_active,
                    expense_type: c.is_vehicle_related ? 'VEHICLE' : (c.is_office_related ? 'OFFICE' : ''),
                    sub_categories: (c.sub_categories || []).map((sc) => ({
                        sub_category_id: sc.id,
                        sub_category_name: sc.name,
                        is_active: sc.is_active
                    }))
                }));
                setCategories(cats);
            }

            setCars(carsRes.data?.data || carsRes.data || []);
            setReferenceDataLoaded(true);
        } catch (error) {
            console.error('Error fetching reference data:', error);
        } finally {
            setReferenceDataLoading(false);
        }
    }, [authHeaders, referenceDataLoaded, referenceDataLoading]);

    const fetchSystemWeeks = useCallback(async () => {
        if (systemWeeksLoaded || systemWeeksLoading) return;
        setSystemWeeksLoading(true);
        try {
            const response = await axios.get(`${rootUrl}/api/system/valid_weeks?past=52`, { headers: authHeaders });
            const validWeeks = Array.isArray(response.data?.valid_weeks) ? response.data.valid_weeks : [];
            const currentWeek = response.data?.system_week ? String(response.data.system_week) : '';

            const weekOptions = validWeeks.map((week) => ({ week_key: String(week), label: `Week ${week}` }));
            setSystemWeeks(weekOptions);
            setSystemWeeksLoaded(true);

            if (!selectedWeekKey && currentWeek) {
                setSelectedWeekKey(currentWeek);
            } else if (!selectedWeekKey && weekOptions.length > 0) {
                setSelectedWeekKey(String(weekOptions[0].week_key));
            }
        } catch (error) {
            console.error('Error fetching system weeks:', error);
        } finally {
            setSystemWeeksLoading(false);
        }
    }, [authHeaders, selectedWeekKey, systemWeeksLoaded, systemWeeksLoading]);

    const periodFilters = useMemo(() => {
        switch (periodView) {
            case 'system-week':
                return selectedWeekKey ? { week_key: selectedWeekKey } : {};
            case 'month':
                return {
                    expense_year: parseInt(selectedMonth.split('-')[0], 10),
                    expense_month: parseInt(selectedMonth.split('-')[1], 10)
                };
            case 'year':
                return { expense_year: selectedYear };
            default:
                return {};
        }
    }, [periodView, selectedMonth, selectedYear, selectedWeekKey]);

    const listFilters = useMemo(() => buildExpenseListParams({
        mode: 'approvals',
        periodFilters,
        filters,
        q: debouncedSearchTerm
    }), [debouncedSearchTerm, filters, periodFilters]);

    const querySignature = useMemo(() => JSON.stringify({
        periodView,
        selectedMonth,
        selectedYear,
        selectedWeekKey,
        expense_type: filters.expense_type,
        category: filters.category,
        sub_category: filters.sub_category,
        plate_no: filters.plate_no,
        min_amount: filters.min_amount,
        max_amount: filters.max_amount,
        search: debouncedSearchTerm
    }), [
        debouncedSearchTerm,
        filters.category,
        filters.expense_type,
        filters.max_amount,
        filters.min_amount,
        filters.plate_no,
        filters.sub_category,
        periodView,
        selectedMonth,
        selectedWeekKey,
        selectedYear
    ]);
    const lastQuerySignatureRef = useRef('');

    const currentPeriodLabel = useMemo(() => {
        switch (periodView) {
            case 'system-week':
                return selectedWeekKey ? `System Week ${selectedWeekKey}` : 'System Week';
            case 'month':
                return monthLabelFormatter.format(new Date(`${selectedMonth}-01T00:00:00`));
            case 'year':
                return `${selectedYear}`;
            default:
                return 'All Time';
        }
    }, [periodView, selectedMonth, selectedYear, selectedWeekKey]);

    const fetchExpenses = useCallback(async () => {
        if (periodView === 'system-week' && !selectedWeekKey) return;

        setLoading(true);
        setCountsLoading(true);
        try {
            const params = {
                page: currentPage,
                pageSize,
                ...listFilters
            };

            const resp = await axios.get(`${rootUrl}/api/expenses`, {
                headers: authHeaders,
                params
            });

            if (resp.data?.success) {
                const data = resp.data.data || [];
                const rows = Array.isArray(data) ? data : (data.items || []);
                setExpenses(rows.map(normalizeExpenseRow));
                setTotalCount(resp.data.total_count || resp.data.total || (Array.isArray(data) ? data.length : 0));
                setCounts(resp.data.counts || null);
            } else {
                setExpenses([]);
                setCounts(null);
            }
        } catch (err) {
            console.error('Error fetching expenses', err);
            showAlert('error', err.response?.data?.message || 'Failed to load expenses');
        } finally {
            setLoading(false);
            setCountsLoading(false);
        }
    }, [authHeaders, currentPage, listFilters, normalizeExpenseRow, pageSize, periodView, selectedWeekKey, showAlert]);

    useEffect(() => {
        if (lastQuerySignatureRef.current !== querySignature) {
            lastQuerySignatureRef.current = querySignature;
            if (currentPage !== 1) {
                setCurrentPage(1);
                return;
            }
        }
        fetchExpenses();
    }, [currentPage, fetchExpenses, querySignature]);

    useEffect(() => {
        if (periodView === 'system-week') {
            fetchSystemWeeks();
        }
    }, [fetchSystemWeeks, periodView]);

    useEffect(() => {
        if (showFilters) {
            fetchReferenceData();
        }
    }, [fetchReferenceData, showFilters]);

    const handleSetFilters = useCallback((nextFilters) => {
        setFilters(nextFilters);
        setCurrentPage(1);
    }, []);

    const handleCategoryChange = useCallback(async (categoryId) => {
        const nextFilters = { ...filters, category: categoryId, sub_category: '' };
        setFilters(nextFilters);
        setCurrentPage(1);

        if (!categoryId) {
            setSubCategories([]);
            return;
        }

        try {
            const response = await axios.get(`${rootUrl}/api/expenses/sub-categories`, {
                headers: authHeaders,
                params: { category_id: categoryId }
            });
            if (response.data?.success) {
                setSubCategories((response.data.data || []).map((sc) => ({
                    sub_category_id: sc.id,
                    sub_category_name: sc.name,
                    category_id: sc.category_id,
                    is_active: sc.is_active
                })));
            }
        } catch (error) {
            console.error('Error fetching sub-categories:', error);
        }
    }, [authHeaders, filters]);

    const handleApprovalAction = async (expenseId, status, notes = '') => {
        try {
            const response = await axios.put(
                `${rootUrl}/api/expenses/${expenseId}/approval`,
                { approval_status: status, approval_notes: notes },
                { headers: authHeaders }
            );
            if (response.data?.success) {
                showAlert('success', `Expense ${status.toLowerCase()} successfully`);
                fetchExpenses();
                return response.data;
            }
            const msg = response.data?.message || 'Failed to update approval status';
            showAlert('error', msg);
            throw new Error(msg);
        } catch (error) {
            console.error('Error updating approval status:', error);
            const msg = error.response?.data?.message || error.message || 'Failed to update approval status';
            showAlert('error', msg);
            throw error;
        }
    };

    const exportToCSV = (rows = [], fileName = 'export') => {
        if (!rows || rows.length === 0) return showAlert('error', 'No data to export');

        const headers = [
            'Expense No',
            'Date',
            'Type',
            'Category',
            'Sub-Category',
            'Description',
            'Amount',
            'Vehicle',
            'Driver',
            'Owner',
            'Payment Method',
            'Invoice Number',
            'Vendor',
            'Approval Status',
            'Approved By',
            'Approval Date',
            'Reimbursement Status',
            'Created By',
            'Created At'
        ];

        const csvRows = rows.map((expense) => ([
            expense.expense_no || '',
            formatDate(expense.expense_date),
            expense.expense_type || '',
            expense.category_name || '',
            expense.sub_category_name || '',
            expense.description || '',
            formatMoney(expense.amount),
            expense.plate_no || '',
            expense.driver_name || '',
            expense.owner_name || '',
            expense.payment_method || '',
            expense.invoice_number || expense.receipt_number || '',
            expense.vendor_name || expense.supplier_name || '',
            expense.approval_status || '',
            expense.approved_by_name || '',
            formatDateTime(expense.approval_date),
            expense.reimbursement_status || '',
            expense.created_by_name || '',
            formatDateTime(expense.created_at)
        ]));

        const csv = [
            headers.join(','),
            ...csvRows.map((row) => row.map((value) => JSON.stringify(value ?? '')).join(','))
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const totalPages = Math.max(1, Math.ceil((totalCount || 0) / pageSize));
    const monthOptions = useMemo(() => buildMonthOptions(24), []);
    const yearOptions = useMemo(() => buildYearOptions(6), []);
    const weekSelectOptions = useMemo(() => systemWeeks.map((week) => ({ value: week.week_key, label: week.label })), [systemWeeks]);
    const monthSelectOptions = useMemo(() => monthOptions.map((option) => ({ value: option.value, label: option.label })), [monthOptions]);
    const yearSelectOptions = useMemo(() => yearOptions.map((year) => ({ value: year, label: String(year) })), [yearOptions]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <Alerty open={open} color={alert.color} message={alert.message} setOpen={setOpen} />

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                <div className="border border-gray-200 bg-white overflow-hidden">
                    <div className="border-b border-gray-200 px-5 sm:px-6 py-5">
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                <div>
                                    <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#FF5733]">Expense Approvals</p>
                                    <h1 className="mt-2 text-3xl font-bold text-[#2a2e30]">Review pending expenses</h1>
                                    <p className="mt-2 text-sm text-gray-600">
                                        Review and approve only expenses waiting for action.
                                    </p>
                                </div>

                                <div className="text-sm text-slate-500 lg:text-right">
                                    Showing: <span className="font-semibold text-slate-700">{currentPeriodLabel}</span>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {['all', 'system-week', 'month', 'year'].map((option) => (
                                    <button
                                        key={option}
                                        onClick={() => {
                                            setPeriodView(option);
                                            setCurrentPage(1);
                                        }}
                                        className={`px-4 py-2 rounded-none border text-sm font-semibold transition-colors shadow-none ${
                                            periodView === option
                                                ? 'bg-[#FF5733] text-white border-[#FF5733]'
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-[#FF5733]/40 hover:text-[#FF5733]'
                                        }`}
                                    >
                                        {option === 'all' && 'All Time'}
                                        {option === 'system-week' && 'System Week'}
                                        {option === 'month' && 'Monthly'}
                                        {option === 'year' && 'Yearly'}
                                    </button>
                                ))}
                            </div>

                            <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
                                <div className="flex flex-wrap items-end gap-3">
                                    {periodView === 'system-week' && (
                                        <div className="min-w-[220px]">
                                            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                                System Week
                                            </label>
                                            <ReactSelect
                                                options={weekSelectOptions}
                                                value={weekSelectOptions.find((option) => option.value === selectedWeekKey) || null}
                                                onChange={(option) => {
                                                    setSelectedWeekKey(option?.value || '');
                                                    setCurrentPage(1);
                                                }}
                                                isDisabled={systemWeeksLoading}
                                                isSearchable
                                                isClearable
                                                placeholder={systemWeeksLoading ? 'Loading weeks...' : 'Select a week'}
                                                styles={selectStyles}
                                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                menuPosition="fixed"
                                            />
                                        </div>
                                    )}

                                    {periodView === 'month' && (
                                        <div className="min-w-[220px]">
                                            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                                Month
                                            </label>
                                            <ReactSelect
                                                options={monthSelectOptions}
                                                value={monthSelectOptions.find((option) => option.value === selectedMonth) || null}
                                                onChange={(option) => {
                                                    setSelectedMonth(option?.value || '');
                                                    setCurrentPage(1);
                                                }}
                                                isSearchable={false}
                                                isClearable={false}
                                                styles={selectStyles}
                                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                menuPosition="fixed"
                                            />
                                        </div>
                                    )}

                                    {periodView === 'year' && (
                                        <div className="min-w-[160px]">
                                            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                                Year
                                            </label>
                                            <ReactSelect
                                                options={yearSelectOptions}
                                                value={yearSelectOptions.find((option) => option.value === selectedYear) || null}
                                                onChange={(option) => {
                                                    setSelectedYear(parseInt(option?.value, 10));
                                                    setCurrentPage(1);
                                                }}
                                                isSearchable={false}
                                                isClearable={false}
                                                styles={selectStyles}
                                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                menuPosition="fixed"
                                            />
                                        </div>
                                    )}
                                </div>

                                <ExpenseHeader
                                    onAdd={() => {}}
                                    canAdd={false}
                                    showFilters={showFilters}
                                    setShowFilters={setShowFilters}
                                    onRefresh={() => fetchExpenses()}
                                    onExport={() => exportToCSV(expenses, 'expense-approvals')}
                                    searchTerm={searchTerm}
                                    setSearchTerm={setSearchTerm}
                                    exportDisabled={expenses.length === 0}
                                />
                            </div>
                        </div>
                    </div>

                    {showFilters && (
                        <div className="border-b border-gray-200 px-5 sm:px-6 py-4">
                            <ExpenseFilters
                                filters={filters}
                                setFilters={handleSetFilters}
                                categories={categories}
                                subCategories={subCategories}
                                cars={cars}
                                handleCategoryChange={handleCategoryChange}
                                clearFilters={() => {
                                    setFilters({
                                        expense_type: '',
                                        category: '',
                                        sub_category: '',
                                        plate_no: '',
                                        min_amount: '',
                                        max_amount: ''
                                    });
                                    setSubCategories([]);
                                    setSearchTerm('');
                                    setCurrentPage(1);
                                }}
                                showApprovalStatus={false}
                            />
                        </div>
                    )}

                    <div className="bg-white">
                        <div className="overflow-x-auto overflow-y-visible rounded-none border-0 bg-white shadow-none">
                            <ExpenseList
                                filteredExpenses={expenses}
                                isMobile={isMobile}
                                loading={loading}
                                formatDate={formatDate}
                                formatCurrency={formatMoney}
                                getApprovalStatusColor={(status) => (status === 'APPROVED' ? 'green' : status === 'REJECTED' ? 'red' : 'amber')}
                                getReimbursementStatusColor={(status) => (status === 'PAID' ? 'green' : status === 'REJECTED' ? 'red' : 'amber')}
                                onView={(expense) => navigate(`/expenses/${expense.expense_id || expense.id}`)}
                                onEdit={canEditExpense ? (expense) => navigate(`/expenses/${expense.expense_id || expense.id}/edit`) : () => {}}
                                onDelete={() => {}}
                                onApprove={(id) => canApproveExpense && handleApprovalAction(id, 'APPROVED')}
                                onReject={(id) => canApproveExpense && handleApprovalAction(id, 'REJECTED')}
                                canEdit={canEditExpense}
                                canDelete={false}
                                canApprove={canApproveExpense}
                                canReject={canApproveExpense}
                            />
                        </div>

                        <div className="border-t border-gray-200 px-5 sm:px-6 py-4">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                pageSize={pageSize}
                                totalCount={totalCount}
                                setCurrentPage={setCurrentPage}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExpenseApprovals;
